import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { AuditorDateRangesPreferences } from '../../../data-models/preferences-v3.data-model';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import {
  computeMaxFuturePeriods,
  computeMaxPastPeriods,
  MAX_RANGE,
} from '../../CompanyProfiles/components/FinancialsDatePickers/financeDatePickerUtils';
import { useAuditorDateConstraints } from '../../CompanyProfiles/components/FinancialsDatePickers/useAuditorDateConstraints';
import {
  futureRangeState,
  pastRangeState,
  selectedEndDateFinancialsState,
  selectedFinancialsRangeState,
  selectedFrequencyFinancialsState,
} from '../../CompanyProfiles/components/FinancialsDatePickers/useFinancialsDateSettings';
import { getDefaultFinancialsEndDate } from '../../CompanyProfiles/state/CompanyFinancialsDateState';
import { allCompanyFinancialsState } from '../../CompanyProfiles/state/CompanyFinancialsState';
import { financialsDateViewByCompanyAtom } from '../../CompanyProfiles/state/FinancialsDateViewPreferences';
import { selectedCompanyIdProfileAtom } from '../../CompanyProfiles/state/UIStateJ';

const AUDITOR_FREQUENCY_MAP: Record<AuditorDateRangesPreferences['defaultFrequency'], KpiPeriod> = {
  quarter: KpiPeriod.quarter,
  month: KpiPeriod.month,
  year: KpiPeriod.year,
};

export function useInitFinancialsDateSettings() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const company = useAtomValue(companyStateJ(companyId));
  const fye = company?.fye ?? 12;
  const financials = useAtomValue(allCompanyFinancialsState(companyId));
  const auditorConstraints = useAuditorDateConstraints();

  const [selectedFrequency, setSelectedFrequency] = useAtom(selectedFrequencyFinancialsState);
  const [selectedRange, setSelectedRange] = useAtom(selectedFinancialsRangeState);
  const [selectedEndDate, setSelectedEndDate] = useAtom(selectedEndDateFinancialsState);
  const setPastRange = useSetAtom(pastRangeState);
  const setFutureRange = useSetAtom(futureRangeState);

  const dateViewPref = useAtomValue(financialsDateViewByCompanyAtom(companyId));
  const previousCompanyIdRef = useRef<number>(companyId);

  return useCallback(() => {
    if (!financials || !company) {
      return false;
    }

    // When navigating between companies, re-initialize from preferences
    if (previousCompanyIdRef.current !== companyId) {
      previousCompanyIdRef.current = companyId;
      const frequency = dateViewPref.frequency;
      const endDate = getDefaultFinancialsEndDate(fye - 1, frequency);
      const prefPastRange = dateViewPref.pastRange;
      // Normalize: if pastRange is Max, futureRange should also be Max
      const prefFutureRange = prefPastRange === MAX_RANGE ? MAX_RANGE : (dateViewPref.futureRange ?? null);

      const resolvedPast =
        prefPastRange === MAX_RANGE ? computeMaxPastPeriods(financials, frequency, endDate) : prefPastRange;
      const resolvedFuture =
        prefFutureRange === MAX_RANGE
          ? computeMaxFuturePeriods(financials, frequency, endDate)
          : (prefFutureRange ?? 0);

      setSelectedFrequency(frequency);
      setPastRange(prefPastRange);
      setFutureRange(prefFutureRange);
      setSelectedRange(resolvedPast + resolvedFuture);
      setSelectedEndDate(endDate);
      return false;
    }

    // Initialize for auditors first (before standard initialization)
    if (auditorConstraints && !selectedFrequency && !selectedRange && !selectedEndDate) {
      const startDate = new Date(auditorConstraints.startDate);
      const endDate = new Date(auditorConstraints.endDate);

      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const startQuarter = Math.floor(startDate.getMonth() / 3) + 1;
        const endQuarter = Math.floor(endDate.getMonth() / 3) + 1;
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        const numQuarters = (endYear - startYear) * 4 + (endQuarter - startQuarter) + 1;

        const frequency = AUDITOR_FREQUENCY_MAP[auditorConstraints.defaultFrequency];
        setSelectedFrequency(frequency);
        setSelectedRange(numQuarters);
        setSelectedEndDate(endDate);
        return true;
      }
    }

    // Standard initialization using preference (org default or per-company override)
    const frequency = selectedFrequency ?? dateViewPref.frequency;

    if (!selectedFrequency) {
      setSelectedFrequency(frequency);
      setPastRange(dateViewPref.pastRange);
      // Normalize: if pastRange is Max, futureRange should also be Max
      const normalizedFuture =
        dateViewPref.pastRange === MAX_RANGE ? MAX_RANGE : (dateViewPref.futureRange ?? null);
      setFutureRange(normalizedFuture);
    }
    if (!selectedRange) {
      const endDate = getDefaultFinancialsEndDate(fye - 1, frequency);
      const resolvedPast =
        dateViewPref.pastRange === MAX_RANGE
          ? computeMaxPastPeriods(financials, frequency, endDate)
          : dateViewPref.pastRange;
      const prefFutureRange =
        dateViewPref.pastRange === MAX_RANGE ? MAX_RANGE : (dateViewPref.futureRange ?? null);
      const resolvedFuture =
        prefFutureRange === MAX_RANGE
          ? computeMaxFuturePeriods(financials, frequency, endDate)
          : (prefFutureRange ?? 0);
      setSelectedRange(resolvedPast + resolvedFuture);
    }
    if (!selectedEndDate) {
      setSelectedEndDate(getDefaultFinancialsEndDate(fye - 1, frequency));
    }

    return Boolean(selectedFrequency) && Boolean(selectedRange) && Boolean(selectedEndDate);
  }, [
    financials,
    company,
    companyId,
    auditorConstraints,
    dateViewPref,
    selectedFrequency,
    fye,
    selectedRange,
    selectedEndDate,
    setSelectedFrequency,
    setSelectedRange,
    setSelectedEndDate,
    setPastRange,
    setFutureRange,
  ]);
}
